import * as Ably from 'ably';
import {EventEmitter} from 'events';
import {uniq} from 'lodash';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  from,
  fromEventPattern,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import {catchError, filter, map, mergeMap, startWith, switchMap} from 'rxjs/operators';

import {isProduction} from '../../../libs/shared/core/src/helpers/environment/envHelpers';
import {
  buildCancelTokenSource,
  CancelToken,
  CancelTokenSource,
} from '../../../libs/shared/core/src/helpers/utilities/asyncCancelHelpers';
import {CompanyModel} from '../../../libs/shared/core/src/models/api/company/companyModel';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {postAsync} from '../../../libs/shared/core/src/services/http/http';
import {pushState} from '../../../libs/shared/core/src/services/http/httpBase';
import {HttpResponseError, isTransientError} from '../../../libs/shared/core/src/services/http/httpError';
import {logger} from '../../../libs/shared/core/src/services/logger/logger';
import {ablyLogger} from '../../../libs/shared/core/src/services/push/ably/ablyLogger';
import {OnlineTeammate} from '../../../src/models/push/onlineTeammateModel';
import {concatMapLatest} from '../../helpers/rx/concatMapLatest';
import {PushDispatcher} from './pushDispatcher';
import {PresenceMember, PushSocket} from './pushSocket';
import {setPushSocketStatusProvider} from './pushSocketStatus';

const initTime = performance.now();

declare module 'ably' {
  namespace Types {
    interface ClientOptions {
      realtimeRequestTimeout?: number;
    }
  }
}

interface Authorizer {
  (channels: ReadonlyArray<string>): Promise<string>;
}

export type ConnectionState = Ably.Types.ConnectionState;

export class AblySocket extends EventEmitter implements PushSocket {
  constructor(
    roomChannelPrefix: string,
    environment: string,
    registerGlobalChannels: boolean,
    getCompany: () => CompanyModel,
    getIsNetworkOnline: () => boolean,
    userId: number,
    options?: {shouldUsePrivateChannelPrefix: boolean; defaultChannels: string[]},
    authorizer?: Authorizer,
  ) {
    super();

    this.registerGlobalChannels = registerGlobalChannels;
    this.getCompany = getCompany;
    this.getIsNetworkOnline = getIsNetworkOnline;
    this.userId = userId;
    this.roomChannelPrefix = roomChannelPrefix;
    this.environment = environment;
    this.pushDispatcher = new PushDispatcher();
    this.shouldUsePrivateChannelPrefix = options?.shouldUsePrivateChannelPrefix ?? true;
    this.defaultChannelNames = options?.defaultChannels ?? this.buildDefaultChannelNames();
    this.subscriptions = [...this.defaultChannelNames];
    this.authorizer = authorizer;
  }

  private readonly userId: number;
  private readonly getCompany: () => CompanyModel;
  private readonly getIsNetworkOnline: () => boolean;
  private readonly registerGlobalChannels: boolean;
  private readonly roomChannelPrefix: string;
  private readonly environment: string;
  private readonly pushDispatcher: PushDispatcher;
  private readonly shouldUsePrivateChannelPrefix: boolean;
  private readonly defaultChannelNames: ReadonlyArray<string>;
  private readonly authorizer?: Authorizer;

  // Default channel name prefixes.
  private readonly privateChannelPrefix = 'private-';
  private readonly presenceChannelPrefix = 'presence-';

  // Stateful socket.
  private readonly client = new AblyClient();
  private get socket() {
    return this.client.socket;
  }
  private isSocketConnected: boolean = false;
  private subscriptions: string[];
  private lastCancelTokenSource: CancelTokenSource | null = null;
  private currentJwt: string | null = null;
  private channelAuthorizations$: Subject<Ably.Types.RealtimeChannelCallbacks> = new Subject();
  private rxSubscription: Subscription | undefined;
  private lastFailureTime: number | undefined;
  private failureCount = 0;
  /** Channels we've been asked to subscribe to but then failed authorization. */
  private readonly channelsPendingAuthorization: Set<Ably.Types.RealtimeChannelCallbacks> = new Set();
  /** Channels we've added a message event listener to. */
  private readonly channelsListening = new WeakSet<Ably.Types.RealtimeChannelCallbacks>();

  get isConnected() {
    return this.isSocketConnected;
  }

  get isSocketOpen() {
    return Boolean(this.socket);
  }

  get socketState() {
    return this.socket?.connection.state;
  }

  get id() {
    if (!this.isSocketConnected || !this.socket || !this.socket.connection || !this.socket.connection.key) {
      return null;
    }

    return this.socket.connection.key;
  }

  connect() {
    // If we're already connected, nothing to do.
    if (this.socket) {
      return false;
    }

    // Otherwise, initiate a connection.
    const socket = this.client.connect({
      environment: this.environment,
      authCallback: async (_tokenDetails, callback) => {
        if (this.lastCancelTokenSource) {
          this.lastCancelTokenSource.cancel();
        }

        this.lastCancelTokenSource = buildCancelTokenSource();
        const channels = this.subscriptions;
        try {
          // The POST response here is an encoded JWT string.
          this.currentJwt = this.authorizer
            ? await this.authorizer(channels)
            : await this.fetchAuthToken(channels, this.lastCancelTokenSource.token);
        } catch (error) {
          /**
           * In case of an error like the conversation has been deleted or the user no longer
           * has access to the conversation but the client still tries to authenticate, just log
           * the error and don't update the current JWT.
           */
          if (error instanceof HttpResponseError) {
            ablyLogger.warn('Failed to authorize Ably', {
              rooms: channels,
              status: error.status,
              body: JSON.stringify(error.body),
            });
          }
        } finally {
          /**
           * Pass an empty string in case current JWT isn't set so that the callback doesn't throw.
           * This usually happens on boot when the first auth request gets cancelled because we do
           * several of them relatively quickly.
           */
          // @ts-expect-error: Error type is ErrorInfo | string, we can't pass null or undefined.
          callback(null, this.currentJwt || '');
        }
      },
      log: {
        // In production, disable all logging from the Ably library.
        // In other envs, enable error logging.
        level: isProduction() ? 0 : 1,
      },
      // Direct events published by this client should not be echoed back to it.
      echoMessages: false,
      realtimeRequestTimeout: 15000,
    });

    // Setup channel authorization observable.
    this.rxSubscription = this.channelAuthorizations$
      .pipe(
        // `authorizeChannelAsync` authorizes all current channel subscriptions at once,
        // so use concatMapLatest to limit how often we require an authorization request.
        concatMapLatest(async (channels): Promise<ReadonlyArray<Ably.Types.RealtimeChannelCallbacks>> => {
          if (this.channelsPendingAuthorization.size > 0) {
            ablyLogger.warn('Subscribing to channels that previously failed authorization', {
              channel_names: [...this.channelsPendingAuthorization].map((channel) => channel.name),
            });
          }

          // Remember all channels we've been asked to subscribe to. If this authorization cycle fails, we can subscribe
          // to them on the next cycle.
          for (const channel of channels) {
            this.channelsPendingAuthorization.add(channel);
          }

          await this.authorizeChannelsAsync();

          const authorizedChannels = [...this.channelsPendingAuthorization];
          this.channelsPendingAuthorization.clear();
          return authorizedChannels;
        }),
        // Flatten arrays into values.
        mergeMap((channels) => from(channels)),
        catchError((error, caught$) => {
          ablyLogger.warn('Failed to authorize Ably', {
            error: JSON.stringify(error),
          });

          // Restart the entire pipeline.
          return caught$;
        }),
      )
      .subscribe((channel) => {
        // If this channel has already been unsubscribed
        // from, skip event subscription.
        if (!this.subscriptions.includes(channel.name)) {
          return;
        }

        // Once a channel is authorized, subscribe to events.
        const onMessage: Ably.Types.messageCallback<Ably.Types.Message> = (event) => {
          const isSelfEvent = Boolean(this.socket && event.connectionId === this.socket.connection.id);

          // We may be allowed to skip this event.
          if (isSelfEvent) {
            return;
          }

          this.pushDispatcher.dispatchEvent(event.name, event.data);
        };

        const onAttach: Ably.Types.errorCallback = (error) => {
          if (!error) {
            return;
          }

          ablyLogger.error(`Failed to attach to Ably channel: ${error.message}`, {
            channel_name: channel.name,
            ably_error_code: error.code,
            reason: JSON.stringify(error),
          });
        };

        if (channel.state === 'failed') {
          ablyLogger.warn('Reattaching failed channel', {
            channel_name: channel.name,
          });
        }

        // Attach the channel even if it's in a failed state.
        channel.attach(onAttach);

        // Add our listener once.
        if (!this.channelsListening.has(channel)) {
          // Our listener will be added as long as the channel is not in a failed state.
          // TODO: Can we avoid relying on Ably's implementation details for this?
          if (channel.state === 'failed') {
            // This shouldn't happen...
            ablyLogger.warn('Ably channel in failed state when trying to subscribe', {
              channel_name: channel.name,
            });
          } else {
            this.channelsListening.add(channel);
          }

          // Add our listener. This will also call attach() again internally, but that should not be a no-op.
          channel.subscribe(onMessage);
        }
      });

    // Register event handlers.
    const connected = () => {
      this.isSocketConnected = true;
      if (this.id) {
        pushState.ablySocketId = this.id;
      }

      this.emit('connected', {shouldResubscribeToRooms: false});
    };

    const disconnected = () => {
      this.isSocketConnected = false;
      this.emit('disconnected', {shouldUnsubscribeFromRooms: false});
    };

    // See https://ably.com/documentation/realtime/connection#connection-states
    socket.connection.on((stateChange) => {
      // The Connection object can emit an event that is not a state change: an `update` event.
      // This happens when there’s a change to connection conditions for which the connection
      // state doesn’t change, e.g. after a reauth.
      if (stateChange.previous === stateChange.current) {
        return;
      }

      if (stateChange.current === 'connected') {
        // A connection exists and is active.
        ablyLogger.info(`Ably connection ${this.lastFailureTime ? 'recovered' : 'connected'}`, {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          reconnect_duration: this.lastFailureTime ? performance.now() - this.lastFailureTime : undefined,
          is_network_monitor_online: this.getIsNetworkOnline(),
        });
        this.lastFailureTime = undefined;
        connected();
        return;
      }

      if (stateChange.current === 'suspended') {
        // A long term failure condition entered after 2 minutes of reconnect
        // attempts in the `disconnected` state. No current connection exists
        // because there is no network connectivity or no host is available.
        ablyLogger.info('Ably connection suspended', {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          is_network_monitor_online: this.getIsNetworkOnline(),
          ably_error_code: stateChange.reason?.code,
          reason: JSON.stringify(stateChange.reason),
        });
        disconnected();
        return;
      }

      if (stateChange.previous !== 'connecting' && stateChange.current === 'disconnected') {
        // A temporary failure condition. Entered when an established connection
        // is dropped, or if a connection attempt was unsuccessful.
        // Upon reconnecting, the library will re-attach to detached channels.
        this.lastFailureTime = performance.now();
        this.failureCount += 1;
        ablyLogger.info('Ably connection disconnected', {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          is_network_monitor_online: this.getIsNetworkOnline(),
          ably_error_code: stateChange.reason?.code,
          reason: JSON.stringify(stateChange.reason),
        });
        disconnected();
        return;
      }

      if (stateChange.current === 'failed') {
        // Unrecoverable failure condition. May be a fatal error received from
        // Ably or some local terminal error. No reconnection attempts are made
        // automatically by the library.
        ablyLogger.error('Ably connection failed', {
          failure_count: this.failureCount,
          uptime: performance.now() - initTime,
          is_network_monitor_online: this.getIsNetworkOnline(),
          ably_error_code: stateChange.reason?.code,
          reason: JSON.stringify(stateChange.reason),
        });
      }

      if (stateChange.current === 'connecting') {
        this.emit('connecting', {previousState: stateChange.previous});
      }
    });

    // If there's no need for the global channels, stop here.
    if (!this.registerGlobalChannels) {
      return true;
    }

    // Subscribe to the default channels.
    const privateTeammateChannel = this.subscribeChannel(this.privateTeammateChannelName);
    privateTeammateChannel.on((stateChange) => {
      if (stateChange.previous === stateChange.current) {
        return;
      }

      switch (stateChange.current) {
        case 'attached':
          ablyLogger.info('Successfully subscribed to private teammate Ably channel', {
            channel_name: this.privateTeammateChannelName,
          });
          break;

        case 'failed':
          ablyLogger.error('Failed to subscribe to private teammate Ably channel', {
            channel_name: this.privateTeammateChannelName,
            ably_error_code: stateChange.reason?.code,
            reason: JSON.stringify(stateChange.reason),
          });
          break;

        default:
          ablyLogger.info(`Private teammate Ably channel is ${stateChange.current}`, {
            channel_name: this.privateTeammateChannelName,
            ably_error_code: stateChange.reason?.code,
            reason: JSON.stringify(stateChange.reason),
          });
          break;
      }
    });

    const companyChannel = this.subscribeChannel(this.presenceCompanyChannelName);
    companyChannel.on('attached', () => {
      ablyLogger.info('Successfully subscribed to company presence Ably channel', {
        channel_name: this.presenceCompanyChannelName,
      });
    });
    companyChannel.on('failed', (stateChange) => {
      ablyLogger.error('Failed to subscribe to company presence Ably channel', {
        channel_name: this.presenceCompanyChannelName,
        ably_error_code: stateChange.reason?.code,
        reason: JSON.stringify(stateChange.reason),
      });
    });

    const companyPresenceChannel = companyChannel.presence;
    companyPresenceChannel.get(undefined, (err, members = []) => {
      if (err) {
        throw err;
      }

      const onlineTeammates = members.map(buildOnlineTeammate);
      this.emit('initialPresence', onlineTeammates);
    });

    companyPresenceChannel.subscribe('enter', (event) => {
      this.emit('teammateOnline', buildOnlineTeammate(event));
    });

    companyPresenceChannel.subscribe('leave', (event) => {
      this.emit('teammateOffline', buildOnlineTeammate(event));
    });

    companyPresenceChannel.enter();

    setPushSocketStatusProvider(() => {
      const channel = this.client.getChannel(this.privateTeammateChannelName);
      const channelState = channel?.state ?? '';

      return {
        connectionState: this.socket?.connection.state ?? 'uninitialized',
        privateChannel: {
          isSubscribed: channelState === 'attached',
          isPending: ['attaching', 'initialized', 'detaching'].includes(channelState),
          isCancelled: ['detached', 'suspended', 'failed'].includes(channelState),
        },
      };
    });
    return true;
  }

  disconnect() {
    // If we're not connected, nothing to do.
    if (!this.socket) {
      return false;
    }

    this.rxSubscription?.unsubscribe();
    this.rxSubscription = undefined;
    this.client.disconnect();
    this.isSocketConnected = false;
    this.pushDispatcher.reset();
    this.emit('disconnected', false);

    return true;
  }

  readonly reconnect = () => {
    if (!this.socket?.connection) {
      return;
    }

    const {state} = this.socket.connection;
    switch (state) {
      case 'disconnected':
      case 'suspended':
        this.socket.connection.connect();
        break;
      default:
    }
  };

  private subscribeChannel(channelName: string) {
    const channel = this.client.getOrInitializeChannel(channelName);

    if (!channel) {
      throw new Error('Cannot register subscription when not connected.');
    }

    this.channelAuthorizations$.next(channel);
    return channel;
  }

  private async authorizeChannelsAsync(): Promise<void> {
    const {socket} = this;
    if (!socket) {
      throw new Error('Cannot authorize channel when not connected');
    }

    return new Promise((resolve, reject) => {
      // This `authorize` call just invokes the socket's `authCallback` to
      // fetch a JWT token encompassing all current channel subscriptions.
      socket.auth.authorize(undefined, undefined, (err) => {
        if (err) {
          reject(err);
          return;
        }

        resolve();
      });
    });
  }

  subscribe(roomName: string, roomSubscribes$?: Subject<string>) {
    // If we're not connected, throw.
    if (!this.socket) {
      throw new Error('Cannot subscribe when not connected.');
    }

    // Join the room.
    const channelName = this.buildChannelName(roomName);
    const isPresenceChannel = channelName.startsWith(this.presenceChannelPrefix);
    const {socket} = this;
    const channel = this.subscribeChannel(channelName);

    if (roomSubscribes$) {
      channel.once('attached', () => {
        roomSubscribes$.next(roomName);
      });
    }

    // Return an unsubscribe method.
    const unsubscribe = () => {
      // If we're disconnected, nothing to do.
      if (!this.socket || this.socket !== socket) {
        return;
      }

      // Otherwise, leave the room.
      channel.detach(() => {
        // Release the channel for garbage collection.
        if (channel.state === 'detached') {
          this.channelsPendingAuthorization.delete(channel);
          // TODO: Encapsulate releasing of channels in the AblyClient class.
          if (this.client.socket === socket) {
            this.client.releaseChannel(channel.name);
          }
        }
      });
      this.channelsListening.delete(channel);
      channel.unsubscribe();
    };

    if (!isPresenceChannel) {
      return {unsubscribe};
    }

    const presenceChannel = channel.presence;

    const getPresence = (callback: (err: Error | null, members?: ReadonlyArray<PresenceMember>) => void) => {
      channel.once('attached', () => {
        presenceChannel.get(undefined, (err, members) => {
          if (err) {
            return callback(new Error(err.message));
          }

          return callback(
            null,
            members?.filter((member) => member.action === 'present'),
          );
        });
      });
    };

    const onPresenceEnter = (callback: (message: PresenceMember) => void) => {
      channel.once('attached', () => {
        presenceChannel.subscribe('enter', callback);
      });
    };
    const onPresenceExit = (callback: (message: PresenceMember) => void) => {
      channel.once('attached', () => {
        presenceChannel.subscribe('leave', callback);
      });
    };

    return {unsubscribe, getPresence, onPresenceEnter, onPresenceExit};
  }

  onPushEvent(eventName: string, callback: (data: any) => void) {
    if (!this.socket) {
      throw new Error('Cannot listen when not connected');
    }

    // Wrap the callback to prevent full crashes.
    const safeCallback = (data: any) => {
      try {
        callback(data);
      } catch (error) {
        reportToBugsnag(error);
      }
    };

    this.pushDispatcher.addCallback(eventName, safeCallback);

    return () => {
      this.pushDispatcher.deleteCallback(eventName, safeCallback);
    };
  }

  onDirectPushEvent(eventName: string, callback: (data: any) => void): () => void {
    // Pusher requires all client-to-client events to be prefixed with 'client-' and in a private/presence room
    // https://pusher.com/docs/channels/using_channels/events#triggering-client-events
    return this.onPushEvent(`client-${eventName}`, callback);
  }

  emitDirectEvent(roomName: string, eventName: string, data: any): void {
    const channel = this.client.getOrInitializeChannel(this.buildChannelName(roomName));

    if (!channel) {
      throw new Error('Cannot emit event when not connected.');
    }

    // All client events need to be prefixed with 'client-'
    // https://pusher.com/docs/channels/using_channels/events#triggering-client-events
    const clientEventName = `client-${eventName}`;
    channel.publish(clientEventName, data);
  }

  ping(): void {
    if (!this.socket) {
      throw new Error('Cannot ping when not connected.');
    }

    this.socket.connection.ping();
  }

  replaceSubscriptions(roomNames: string[]) {
    const channelNames = roomNames.map((roomName) => this.buildChannelName(roomName));
    this.subscriptions = uniq([...this.defaultChannelNames, ...channelNames]);
  }

  private async fetchAuthToken(
    channels: ReadonlyArray<string>,
    cancelToken: CancelToken,
    options?: {isRetry: boolean},
  ): Promise<string> {
    try {
      return await postAsync(
        `${this.getCompany().url}/ably/auth`,
        {
          rooms: channels,
        },
        // Note: ensure that this timeout is lower than
        // Ably's configured realtimeRequestTimeout.
        {cancelToken, timeout: 10000},
      );
    } catch (error) {
      // Retry once.
      if (isTransientError(error) && !options?.isRetry) {
        ablyLogger.warn('Failed to authorize Ably, retrying', {
          rooms: channels,
          status: error.status,
          body: JSON.stringify(error.body),
        });

        return this.fetchAuthToken(channels, cancelToken, {isRetry: true});
      }

      throw error;
    }
  }

  private buildDefaultChannelNames() {
    return [
      this.privateTeammateChannelName,
      this.privateTeammateFolderChannelName,
      this.presenceCompanyChannelName,
    ];
  }

  private get privateTeammateChannelName() {
    return this.buildPrivateChannelName(`teammate-${this.userId}`);
  }

  private get privateTeammateFolderChannelName() {
    return this.buildPrivateChannelName(`teammatefolder-${this.userId}`);
  }

  private get presenceCompanyChannelName() {
    return `${this.presenceChannelPrefix}${this.roomChannelPrefix}company-${this.getCompany().id}`;
  }

  private buildPrivateChannelName(roomName: string) {
    return `${this.privateChannelPrefix}${this.roomChannelPrefix}${roomName}`;
  }

  private buildChannelName(roomName: string) {
    return this.shouldUsePrivateChannelPrefix
      ? this.buildPrivateChannelName(roomName)
      : `${this.roomChannelPrefix}${roomName}`;
  }

  getIsReceiving(roomName: string): boolean {
    const channelName = this.buildChannelName(roomName);

    return this.client.getIsReceiving(channelName);
  }

  getIsReceiving$(roomName: string): Observable<boolean> {
    const channelName = this.buildChannelName(roomName);

    return this.client.getIsReceiving$(channelName);
  }
}

/*
 * Helpers.
 */

function buildOnlineTeammate(presenceMessage: Ably.Types.PresenceMessage): OnlineTeammate {
  return {
    teammateId: Number(presenceMessage.clientId),
    connectionId: presenceMessage.connectionId,
  };
}

type AblyRealtimeChannel = Ably.Types.RealtimeChannelCallbacks;

class AblyClient {
  private readonly connection$ = new BehaviorSubject<AblyConnection | undefined>(undefined);

  private get connection() {
    return this.connection$.value;
  }

  // TODO: Encapsulate the Ably.Realtime object.
  get socket(): Ably.Realtime | undefined {
    return this.connection?.socket;
  }

  // TODO: Encapsulate the connection lifecycle.
  connect(options: Ably.Types.ClientOptions): Ably.Realtime {
    if (this.connection !== undefined) {
      logger.warn('AblyClient: connect() called without a prior disconnect()');
    }

    const connection = new AblyConnection(options);
    this.connection$.next(connection);
    return connection.socket;
  }

  // TODO: Encapsulate the connection lifecycle.
  disconnect(): void {
    if (this.connection) {
      this.connection.socket.close();
    } else {
      logger.warn('AblyClient: disconnect() called without a prior connect()');
    }

    this.connection$.next(undefined);
  }

  getChannel(channelName: string): AblyRealtimeChannel | undefined {
    return this.connection?.getChannel(channelName);
  }

  getOrInitializeChannel(channelName: string): AblyRealtimeChannel | undefined {
    return this.connection?.getOrInitializeChannel(channelName);
  }

  releaseChannel(channelName: string) {
    this.connection?.releaseChannel(channelName);
  }

  getIsReceiving(channelName: string) {
    return this.connection?.getIsReceiving(channelName) ?? false;
  }

  getIsReceiving$(channelName: string) {
    return this.connection$.pipe(
      switchMap((connection) => (connection ? connection.getIsReceiving$(channelName) : of(false))),
    );
  }
}

class AblyConnection {
  readonly socket: Ably.Realtime;

  private readonly channels = new Map<string, AblyRealtimeChannel>();
  private readonly channelsChange$ = new Subject<[string, AblyRealtimeChannel | undefined]>();

  constructor(options: Ably.Types.ClientOptions) {
    this.socket = new Ably.Realtime(options);
  }

  getChannel(channelName: string): AblyRealtimeChannel | undefined {
    return this.channels.get(channelName);
  }

  getOrInitializeChannel(channelName: string): AblyRealtimeChannel {
    const initializedChannel = this.channels.get(channelName);
    const channel = this.socket.channels.get(channelName);

    if (!initializedChannel) {
      this.channels.set(channelName, channel);
      this.channelsChange$.next([channelName, channel]);
    } else if (initializedChannel !== channel) {
      logger.warn(`AblyConnection: channel already exists with a different instance`, {
        channel_name: channelName,
      });
    }

    return channel;
  }

  getChannel$(channelName: string): Observable<AblyRealtimeChannel | undefined> {
    return merge(
      defer(() => of(this.getChannel(channelName))),
      this.channelsChange$.pipe(
        filter(([updatedChannelName]) => updatedChannelName === channelName),
        map(([, channel]) => channel),
      ),
    );
  }

  getIsReceiving(channelName: string) {
    return this.isConnected && this.getIsAttached(channelName);
  }

  getIsReceiving$(channelName: string) {
    return combineLatest([this.isConnected$, this.getIsAttached$(channelName)]).pipe(
      map(([isConnected, isAttached]) => isConnected && isAttached),
    );
  }

  get isConnected() {
    return this.socket.connection.state === 'connected';
  }

  get isConnected$() {
    const {connection} = this.socket;

    return fromEventPattern<Ably.Types.ConnectionStateChange>(
      (handler) => connection.on(handler),
      (handler) => connection.off(handler),
    ).pipe(
      startWith(undefined),
      map(() => this.isConnected),
    );
  }

  getIsAttached(channelName: string) {
    const channel = this.getChannel(channelName);

    return channel?.state === 'attached';
  }

  getIsAttached$(channelName: string) {
    return this.getChannel$(channelName).pipe(
      switchMap((channel) => {
        if (!channel) {
          return of(false);
        }

        return fromEventPattern<Ably.Types.ChannelStateChange>(
          (handler) => channel.on(handler),
          (handler) => channel.off(handler),
        ).pipe(
          startWith(undefined),
          map(() => channel?.state === 'attached'),
        );
      }),
    );
  }

  releaseChannel(channelName: string) {
    // This throws if the channel cannot be released.
    this.socket.channels.release(channelName);

    this.channels.delete(channelName);
    this.channelsChange$.next([channelName, undefined]);
  }
}
